// Global Instructions Rule Applied!
//
// Derive pipeline + stage options from the cfg-driven `globalConfigs` slice, mirroring
// the desktop `useDealManager` logic (defaultPipelineId + getPipelineStages) but as pure
// functions over the slice arrays so the mobile filter/selector layers can call them
// without a hook. Stages are constrained per-pipeline by the `pipeline_config` junction
// (the `visible_{tbl,kb}` flags) and ordered by the per-view sequence — exactly what the
// "only certain stages are available for certain pipelines" rule requires.

import { PIPELINE_TYPE_DEAL_ORIGINATION, PIPELINE_TYPE_FUNDRAISING } from '../../../../state_manager/globals.ts'

export type PipelineViewType = 'tbl' | 'kb'

export interface PipelineOption {
  id: string
  name: string
  isDefault: boolean
}

export interface StageOption {
  value: string
  label: string
  colour?: string
}

/** The subset of the `globalConfigs` slice these helpers read. */
export interface PipelineConfigsSlice {
  pipelines?: any[]
  pipeline_configs?: any[]
  pipeline_stages?: any[]
}

export { PIPELINE_TYPE_DEAL_ORIGINATION, PIPELINE_TYPE_FUNDRAISING }

/** Pipelines of a given type (e.g. Deal Origination / Fundraising), default-first then by name. */
export function getPipelinesByType(gc: PipelineConfigsSlice | null | undefined, pipelineType: string): PipelineOption[] {
  const pipelines = (gc?.pipelines ?? []).filter((p: any) => p && p.type === pipelineType && p.id)
  return pipelines
    .map((p: any) => ({ id: String(p.id), name: String(p.name ?? 'Pipeline'), isDefault: p.default === true }))
    .sort((a, b) => (a.isDefault === b.isDefault ? a.name.localeCompare(b.name) : a.isDefault ? -1 : 1))
}

/** The default pipeline id for a type, or null. */
export function getDefaultPipelineId(gc: PipelineConfigsSlice | null | undefined, pipelineType: string): string | null {
  const p = (gc?.pipelines ?? []).find((x: any) => x && x.default === true && x.type === pipelineType)
  return p?.id ? String(p.id) : null
}

/**
 * Resolve a fund's Fundraising pipeline id. Mirrors the backend
 * `get_fund_pipeline_origination` + default fallback: use the fund's `pipeline_origination`
 * when it points at a Fundraising-type pipeline, else the default Fundraising pipeline.
 * Drives the prospects Stage filter so its options follow the selected fund's pipeline.
 */
export function resolveFundraisingPipelineId(
  gc: (PipelineConfigsSlice & { funds?: any[] }) | null | undefined,
  fundId: string | null | undefined
): string | null {
  const fallback = getDefaultPipelineId(gc, PIPELINE_TYPE_FUNDRAISING)
  if (!fundId) return fallback
  const fund = (gc?.funds ?? []).find((f: any) => f && String(f.id) === String(fundId))
  const po = fund?.pipeline_origination
  const isFundraising =
    po && (gc?.pipelines ?? []).some((p: any) => p && String(p.id) === String(po) && p.type === PIPELINE_TYPE_FUNDRAISING)
  return isFundraising ? String(po) : fallback
}

/**
 * Ordered stage options for a pipeline + view, joining `pipeline_config` (visibility +
 * per-view sequence + colour) to `pipeline_stages` (name, fallback colour). Empty when the
 * pipeline id is missing or has no visible stages.
 */
export function getStageOptions(
  gc: PipelineConfigsSlice | null | undefined,
  pipelineId: string | null | undefined,
  viewType: PipelineViewType = 'tbl'
): StageOption[] {
  if (!pipelineId) return []
  const visibleField = `visible_${viewType}`
  const seqField = `sequence_${viewType}`

  const stagesById: Record<string, any> = {}
  for (const s of gc?.pipeline_stages ?? []) if (s && s.id) stagesById[String(s.id)] = s

  const rows = (gc?.pipeline_configs ?? [])
    .filter((c: any) => c && c.pipeline === pipelineId && c[visibleField] === true)
    .map((c: any) => {
      const stage = stagesById[String(c.stage)]
      if (!stage) return null
      return {
        // The value must be the pipeline_config row id (c.id), NOT the stage id:
        // both the deals lean RPC (pd.stage = ANY(...)) and the prospects RPC
        // (pp.stage = ANY(...)) match the Stage filter against pipeline_config.id.
        value: String(c.id),
        label: String(stage.name ?? 'Stage'),
        colour: c.colour ?? stage.colour ?? undefined,
        sequence: c[seqField] ?? stage.sequence ?? Number.MAX_SAFE_INTEGER
      }
    })
    .filter(Boolean) as Array<StageOption & { sequence: number }>

  rows.sort((a, b) => a.sequence - b.sequence)
  return rows.map(({ value, label, colour }) => ({ value, label, colour }))
}
